// ============================================================
// DM-LMS — useAuth Hook + AuthProvider
// DRY: one provider holds the single /api/auth/me fetch; every
// useAuth() caller reads the same state. Mount <AuthProvider> once,
// above <App />.
// ============================================================

import { createContext, createElement, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode }      from "react";
import type { User }           from "@dm-lms/shared/types";
import { DEV_USERS }           from "@dm-lms/shared/constants";
import { API_URL }             from "./useApi.js";
import { devRoleHeaders, getDevRole } from "./devRole.js";
import { authClient }          from "../lib/auth-client.js";

interface AuthState {
  user:      User | null;
  isLoading: boolean;
  error:     string | null;
}

export interface AuthContextValue extends AuthState {
  /** Re-reads /api/auth/me and updates every consumer. Does not set isLoading. */
  refetch: () => Promise<void>;
  /**
   * Ends the session via Better Auth's POST /sign-out, then does a full-page
   * navigation to /app/login so no provider keeps cached state. Resolves false
   * (user stays signed in) on any failure; "session not found" counts as success.
   */
  signOut: () => Promise<boolean>;
}

const LOGIN_PATH = "/app/login";

// Better Auth answers 400 "Session not found" when no session cookie is sent,
// i.e. the user is already signed out. The client resolves { data, error } with
// error = { message?, status, statusText } rather than throwing on HTTP errors.
function isSessionNotFound(error: { status?: number; message?: string }): boolean {
  if (error.status === 400) return true;
  return /session not found/i.test(error.message ?? "");
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // DEV ONLY: a role picked in DevRoleSwitcher resolves synchronously, skipping /api/auth/me.
  const devRole = import.meta.env.DEV ? getDevRole() : null;
  const [state, setState] = useState<AuthState>({
    user:      devRole ? DEV_USERS[devRole] : null,
    isLoading: !devRole,
    error:     null,
  });

  const refetch = useCallback(async () => {
    if (devRole) return;
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: "include", headers: devRoleHeaders() });
      if (!res.ok) {
        setState({ user: null, isLoading: false, error: null });
        return;
      }
      const json = (await res.json()) as { ok: true; data: User };
      setState({ user: json.data, isLoading: false, error: null });
    } catch {
      setState({ user: null, isLoading: false, error: "Failed to load session" });
    }
  }, [devRole]);

  const signOut = useCallback(async (): Promise<boolean> => {
    try {
      const result = await authClient.signOut();
      if (result.error && !isSessionNotFound(result.error)) return false;
    } catch {
      return false;
    }
    window.location.assign(LOGIN_PATH);
    return true;
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return createElement(AuthContext.Provider, { value: { ...state, refetch, signOut } }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
